import React, { useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import Alert from 'src/shared/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/shared/components/ui/dialog';
import type { CommitSummary } from '../../commit';

type CommitSummaryBannerProps = {
    summary: CommitSummary;
    isCommitting: boolean;
    onReupload: () => void;
    onDownloadFailed: () => void;
};

// Post-commit headline. Two visual modes:
//   - success: count of created / updated / unchanged-skipped
//   - failure: same counts + an action group (Download / Re-upload)
// Hidden while a commit is in flight (CommitProgressBar covers that state).
export const CommitSummaryBanner = ({ summary, isCommitting, onReupload, onDownloadFailed }: CommitSummaryBannerProps) => {
    // Re-upload risks creating duplicates of any rows already onboarded during
    // this attempt (engine has nothing to dedupe against — those sites came
    // from rows without an Internal ID). When that risk exists we intercept
    // the click with a confirm dialog. Pure-update failure batches are safe
    // (every row has an Internal ID) → re-upload proceeds directly.
    const [confirmReuploadOpen, setConfirmReuploadOpen] = useState(false);
    const handleReuploadClick = () => {
        if (summary.created > 0) {
            setConfirmReuploadOpen(true);
        } else {
            onReupload();
        }
    };
    const confirmReupload = () => {
        setConfirmReuploadOpen(false);
        onReupload();
    };

    if (isCommitting) return null;
    if (summary.failed > 0) {
        return (
            <>
                <Alert severity='error' data-testid='commit-summary-banner'>
                    <div className='flex items-center justify-between gap-4'>
                        <span>
                            {summary.created} created · {summary.updated} updated · <strong>{summary.failed} failed</strong>
                            {summary.unchangedSkipped > 0 && ` · ${summary.unchangedSkipped} unchanged skipped`}
                        </span>
                        <div className='flex items-center gap-2'>
                            <PrimaryButton onClick={onDownloadFailed} data-testid='commit-download-failed-button'>
                                <span className='inline-flex items-center gap-2'>
                                    <Download className='w-4 h-4' /> Download failed rows
                                </span>
                            </PrimaryButton>
                            <PrimaryButton onClick={handleReuploadClick} data-testid='commit-reupload-button'>
                                <span className='inline-flex items-center gap-2'>
                                    <Upload className='w-4 h-4' /> Re-upload fixed CSV
                                </span>
                            </PrimaryButton>
                        </div>
                    </div>
                </Alert>
                <ReuploadConfirmDialog
                    open={confirmReuploadOpen}
                    onOpenChange={setConfirmReuploadOpen}
                    alreadyCreatedCount={summary.created}
                    onConfirm={confirmReupload}
                />
            </>
        );
    }
    return (
        <Alert severity='success' data-testid='commit-summary-banner'>
            {summary.created} {summary.created === 1 ? 'site' : 'sites'} created
            {summary.updated > 0 && ` · ${summary.updated} updated`}
            {summary.unchangedSkipped > 0 && ` · ${summary.unchangedSkipped} unchanged skipped`}
        </Alert>
    );
};

const ReuploadConfirmDialog = ({
    open,
    onOpenChange,
    alreadyCreatedCount,
    onConfirm,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    alreadyCreatedCount: number;
    onConfirm: () => void;
}) => {
    const noun = alreadyCreatedCount === 1 ? 'site' : 'sites';
    const itThem = alreadyCreatedCount === 1 ? 'it' : 'them';
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid='commit-reupload-confirm' className='bg-white rounded-md'>
                <DialogHeader>
                    <DialogTitle>Re-upload may create duplicate sites</DialogTitle>
                    <DialogDescription>
                        You&apos;ve already onboarded {alreadyCreatedCount} {noun} in this attempt. Re-uploading the same CSV will add a
                        second copy of {itThem}.
                    </DialogDescription>
                </DialogHeader>
                <div className='text-sm text-grey-100'>
                    To avoid duplicates, remove the already-onboarded rows from your CSV before re-uploading.
                </div>
                <DialogFooter>
                    <SecondaryButton onClick={() => onOpenChange(false)} data-testid='commit-reupload-cancel'>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={onConfirm} data-testid='commit-reupload-confirm-button'>
                        Re-upload anyway
                    </PrimaryButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
